import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Cookie } from 'lucide-react';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-sauna-beige py-12 px-4">
      <Helmet>
        <title>Cookie Policy - SaunaDip</title>
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-sauna-navy flex items-center justify-center gap-2">
            <Cookie className="h-8 w-8 text-sauna-orange" />
            Cookie Policy
          </h1>
          <div className="h-1 w-32 divider-gradient rounded-full mx-auto mt-4"></div>
        </div>
        <Card className="border-none shadow-lg">
          <CardContent className="p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">Table of Contents</h2>
              <ul className="list-disc list-inside space-y-1 text-sauna-navy/80">
                <li><a href="#introduction" className="text-sauna-orange hover:underline">1. Introduction</a></li>
                <li><a href="#what-are-cookies" className="text-sauna-orange hover:underline">2. What are Cookies?</a></li>
                <li><a href="#how-we-use-cookies" className="text-sauna-orange hover:underline">3. How We Use Cookies</a></li>
                <li><a href="#types-of-cookies" className="text-sauna-orange hover:underline">4. Types of Cookies We Use</a></li>
                <li><a href="#manage-cookies" className="text-sauna-orange hover:underline">5. Managing Your Cookies</a></li>
                <li><a href="#changes" className="text-sauna-orange hover:underline">6. Changes to This Cookie Policy</a></li>
                <li><a href="#contact" className="text-sauna-orange hover:underline">7. Contact Us</a></li>
              </ul>
            </div>

            <section id="introduction" className="mb-12">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">1. Introduction</h2>
              <p className="text-sauna-navy/90">This Cookie Policy explains how SaunaDip uses cookies and similar technologies when you visit our website.</p>
            </section>

            <section id="what-are-cookies" className="mb-12">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">2. What are Cookies?</h2>
              <p className="text-sauna-navy/90">Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work, or work more efficiently, as well as to provide reporting information.</p>
            </section>

            <section id="how-we-use-cookies" className="mb-12">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">3. How We Use Cookies</h2>
              <p className="text-sauna-navy/90">We use cookies for several reasons, including:</p>
              <ul className="list-disc list-inside ml-4 text-sauna-navy/80">
                <li>To ensure the website functions correctly.</li>
                <li>To understand how you use our website and improve your experience.</li>
                <li>To remember your preferences.</li>
                <li>For analytics and advertising purposes.</li>
              </ul>
            </section>

            <section id="types-of-cookies" className="mb-12">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">4. Types of Cookies We Use</h2>
              <p className="text-sauna-navy/90">We may use the following types of cookies:</p>
              <ul className="list-disc list-inside ml-4 text-sauna-navy/80">
                <li>Strictly Necessary Cookies: Essential for the website to function.</li>
                <li>Performance and Analytics Cookies: Collect information about how visitors use the website.</li>
                <li>Functionality Cookies: Remember your preferences and choices.</li>
                <li>Advertising Cookies: Used to deliver relevant advertisements.</li>
              </ul>
              <p className="text-sauna-navy/90 mt-4">Here is a list of the specific cookies used on our website:</p>
              <ul className="list-disc list-inside ml-4 text-sauna-navy/80">
                <li>
                  <strong>`saunadip-cookie-consent`</strong>:
                  Purpose: Stores your consent to cookie usage.
                  Duration: Persistent (typically one year or configured duration).
                </li>
                <li>
                  <strong>`sidebar:state`</strong>:
                  Purpose: Remembers your sidebar preference (expanded or collapsed).
                  Duration: 7 days.
                </li>
                <li>
                  <strong>Mixpanel cookies</strong>:
                  Purpose: Used for website analytics and user behavior tracking.
                  Duration: Varies (refer to Mixpanel documentation for details).
                </li>
                <li>
                  <strong>Microsoft Clarity cookies</strong>:
                  Purpose: Used for heatmaps, session recordings, and user behavior analysis.
                  Duration: Varies (refer to Microsoft Clarity documentation for details).
                </li>
                <li>
                  <strong>Plausible Analytics cookies (potential)</strong>:
                  Purpose: Used for website analytics. Plausible aims to be cookie-free, but specific configurations or future updates might use cookies.
                  Duration: Varies if used (refer to Plausible documentation for details).
                </li>
              </ul>
            </section>

            <section id="manage-cookies" className="mb-12">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">5. Managing Your Cookies</h2>
              <p className="text-sauna-navy/90">You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by [Explain how users can manage cookies, e.g., through browser settings, a cookie consent banner, or a link to a cookie management tool].</p>
            </section>

            <section id="changes" className="mb-12">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">6. Changes to This Cookie Policy</h2>
              <p className="text-sauna-navy/90">We may update our Cookie Policy from time to time. We will notify you of any changes by posting the new Cookie Policy on this page. You are advised to review this Cookie Policy periodically for any changes.</p>
            </section>

            <section id="contact" className="mb-4">
              <h2 className="text-2xl font-bold text-sauna-navy flex items-center mb-6">7. Contact Us</h2>
              <p className="text-sauna-navy/90">If you have any questions about this Cookie Policy, please contact us at [Your Contact Information].</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicy; 